import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { LoadingButton } from "@mui/lab";
import { Box, Grid, TextField, Typography, styled } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { LoadingSpinner } from "../../../components/shared/LoadingSpinner";
import { InvoicingOptions } from "../../../models/employers/invoicingOptions";
import { useConfig } from "../../../utils/useConfig";
import { useData } from "../../../utils/useData";

const OptionsTitle = styled(Typography)({
  fontWeight: 600,
  fontSize: 16,
  marginBottom: 16
});

const Invoicing = () => {
  const { employer } = useData();
  const { config } = useConfig();
  const { getToken } = useKindeAuth();
  const [initialFormValues, setInitialFormValues] = useState<InvoicingOptions>({
    reference: ""
  });

  useEffect(() => {
    if (employer.data) {
      setInitialFormValues({
        reference: employer.data.invoicingOptions?.reference || ""
      });
    }
  }, [employer.data]);

  const updateInvoicingOptionsMutation = useMutation(
    async (invoicingOptions: InvoicingOptions) => {
      const token = await getToken();
      const response = await fetch(
        `${config?.API_URL}/employers/${employer.data?.id}/invoicing-options`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            reference:
              invoicingOptions.reference.length > 0
                ? invoicingOptions.reference
                : null
          })
        }
      );

      if (!response.ok) {
        throw new Error("There was a problem updating invoicing preferences");
      }
    },
    {
      onSuccess: () => {
        toast.success("Invoicing preferences updated successfully");
        employer.refetch();
      },

      onError: (error) => {
        console.error("Error updating invoicing preferences:", error);
        toast.error("Failed to update invoicing preferences");
      }
    }
  );

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await updateInvoicingOptionsMutation.mutateAsync(values);
        setSubmitting(false);
      } catch (error) {
        console.error("Error saving invoicing preferences:", error);
        setSubmitting(false);
      }
    }
  });

  const employerIsLoading = employer.isIdle || employer.isLoading;

  if (employerIsLoading) return <LoadingSpinner />;

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box sx={{ p: 3, width: "100%" }}>
        <Grid container item xs={6} spacing={2}>
          <Grid item xs={12}>
            <OptionsTitle>Reference for Invoices</OptionsTitle>
            <TextField
              name="reference"
              value={formik.values.reference}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 50 && /^[a-zA-Z0-9-]*$/.test(value)) {
                  formik.setFieldValue("reference", value);
                }
              }}
              color="primary"
              size="small"
              placeholder="eg. Purchase order number"
              fullWidth
            />
          </Grid>

          <Grid item xs={12}>
            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              loading={formik.isSubmitting}
              disabled={!formik.dirty}
              fullWidth
              sx={{ mt: 2 }}
            >
              Save
            </LoadingButton>
          </Grid>
        </Grid>
      </Box>
    </form>
  );
};

export { Invoicing };
